import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Star, MessageCircle, ShoppingBag, Sparkles } from "lucide-react";
import axios from "axios";
import GTFLogo from "@/components/GTFLogo";
import VerifiedBadge from "@/components/VerifiedBadge";
import ShareButton from "@/components/ShareButton";
import RichDescription from "@/components/RichDescription";
import FullscreenImage from "@/components/FullscreenImage";
import { formatPriceText } from "@/lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_IMG = "https://images.unsplash.com/photo-1560493676-04071c5f467b?auto=format&fit=crop&w=1200&q=80";

/**
 * PublicListing — a shareable, no-login-required view of a GTF post.
 * When the visitor clicks any action (Contact/Order/Chat) we deep-link them to
 * `/login?next=/marketplace/{id}` so they land back on the full post after auth.
 */
export default function PublicListing() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/listings/${id}`)
      .then((r) => setItem(r.data))
      .catch((e) => setError(e.response?.status === 404 ? "This post is no longer available." : "Unable to load this post."));
  }, [id]);

  const loginNext = `/login?next=${encodeURIComponent(`/marketplace/${id}`)}`;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <GTFLogo className="h-10" />
        <div className="mt-6 rounded-2xl bg-white border border-gray-200 p-8 max-w-md">
          <div className="text-lg font-semibold text-gray-900">{error}</div>
          <Link to="/" className="mt-4 inline-block px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold">Open GTF</Link>
        </div>
      </div>
    );
  }
  if (!item) return <div className="min-h-screen flex items-center justify-center text-emerald-700">Loading...</div>;

  const images = item.images?.length ? item.images : [FALLBACK_IMG];
  const location = [item.seller_village, item.seller_district, item.seller_state].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Public header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2"><GTFLogo className="h-8" /></Link>
          <div className="ml-auto flex items-center gap-2">
            <Link to={loginNext} data-testid="public-login-cta" className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold">Login / Sign up</Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Images */}
        <div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl overflow-hidden bg-white border border-gray-100 aspect-square cursor-zoom-in"
            onClick={() => setLightbox(true)}>
            <img src={images[imgIdx]} alt={item.name} data-testid="public-main-image" className="w-full h-full object-cover" />
          </motion.div>
          {images.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {images.map((im, i) => (
                <button key={`thumb-${i}`} onClick={() => setImgIdx(i)}
                  className={`rounded-xl overflow-hidden border-2 aspect-square ${i === imgIdx ? "border-emerald-500" : "border-transparent"}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] uppercase tracking-widest">
              {item.kind} · {item.category}
              {item.is_featured && <span className="inline-flex items-center gap-1 ml-1 px-2 py-0.5 rounded-full bg-amber-500 text-white"><Sparkles className="h-2.5 w-2.5" />Featured</span>}
            </div>
            <div className="ml-auto">
              <ShareButton id={item.id} title={item.name} price={item.price} location={location} />
            </div>
          </div>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">{item.name}</h1>
          <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{location || "—"}</span>
            {item.avg_rating && <span className="flex items-center gap-1 text-amber-500"><Star className="h-3.5 w-3.5 fill-amber-500" />{item.avg_rating} · {item.review_count} reviews</span>}
          </div>
          <div className="mt-6 text-4xl font-bold text-emerald-700">{formatPriceText(item)}</div>

          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-2">About this {item.kind || "post"}</div>
            <RichDescription text={item.description} />
          </div>

          <div className="mt-6 rounded-2xl bg-white border border-gray-100 p-5">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold">{item.owner_name?.[0]}</div>
              <div>
                <div className="font-semibold text-gray-900 flex items-center gap-2">{item.owner_name}{item.owner_verified && <VerifiedBadge small />}</div>
                <div className="text-xs text-gray-500 uppercase tracking-widest">{item.owner_role?.replace("_", " ")}</div>
              </div>
            </div>
            <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
              🔒 Login to view seller contact details, place an order, or start a chat.
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Link to={loginNext} data-testid="public-interested-cta" className="px-4 py-3 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold flex items-center justify-center gap-2">
              <Star className="h-4 w-4" />Interested
            </Link>
            <Link to={loginNext} data-testid="public-buy-cta" className="px-4 py-3 rounded-full bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 shadow-lg">
              <ShoppingBag className="h-4 w-4" />Buy Now
            </Link>
            <Link to={loginNext} data-testid="public-chat-cta" className="px-4 py-3 rounded-full bg-white border border-gray-200 text-gray-800 font-semibold flex items-center justify-center gap-2">
              <MessageCircle className="h-4 w-4" />Chat
            </Link>
          </div>

          <div className="mt-6 text-center text-xs text-gray-500">
            New to GTF?{" "}
            <Link to="/role" className="text-emerald-700 font-semibold">Create free account →</Link>
          </div>
        </motion.div>
      </div>
      {lightbox && (
        <FullscreenImage images={images} startIndex={imgIdx} onClose={() => setLightbox(false)} />
      )}
    </div>
  );
}
